import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { View, Platform, Alert, KeyboardAvoidingView, ActivityIndicator, Keyboard, Pressable, Modal, ScrollView } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import * as Animatable from "react-native-animatable";
import { FontAwesome, FontAwesome6, AntDesign, Entypo, MaterialIcons } from "@expo/vector-icons";
import { BlurView } from "expo-blur";
import LottieView from "lottie-react-native";
import debounce from "lodash.debounce";
import Slider from "@react-native-community/slider";
import { Button, Text, YStack, XStack, TextArea, Sheet, Dialog, H4, SizableText } from "tamagui";
import styles from '@stylesheet';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import SearchComponent from '@components/Global/SearchComponent/SearchBar';
import PostToFeed from "./PostToFeed";
import GenerateSchemaForm from '@hooks/generateSchemaForm';
import HorizontalCarousel from '@components/Global/Carousel/HorizontalCarousel';
import { AI_API_URL, HOST } from '@api/constant';
import { handleGetLocation, refreshLocation } from '@hooks/handleGetLocation';
import { DefaultRootState } from '@reduxLocal/persistState';
import apis, { noMonitor, monitorFail } from "@api/api";
import { t } from "i18next";
import * as ScreenOrientation from 'expo-screen-orientation';
import { useIsFreePlanWithContext } from '@utils/billingUtils';
import { checkPostRateLimit, storeLastPostTimestamp, formatRateLimitMessage } from '@utils/postRateLimiter';

const FeedHeader = ({ searchActive, refetch, setSearchActive, distanceCallback }) => {
  const theme = themeSelector();
  const dispatch = useDispatch();
  const [distanceValue, setDistanceValue] = useState(1);
  const animRef = useRef(null);
  const [feedType, setFeedType] = useState(false);
  const selector = useSelector((state: DefaultRootState) => state.app.location);
  const [item, setItem] = useState(null);
  const [textInputValue, setTextInputValue] = useState(''); // State for the AI content text area
  const [AIResponse, setAIResponse] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const formRef = useRef(null);
  const [activeProduct, setActiveProduct] = useState(null);
  const [open, setOpen] = useState(false);
  const [selectedIntegrations, setSelectedIntegrations] = useState({});
  const isFreePlan = useIsFreePlanWithContext();

  // New state for managing the editable content and the original prompt
  const [editableContent, setEditableContent] = useState('');
  const [originalPromptBody, setOriginalPromptBody] = useState(null);

  // Effect to update the editable text area when the AI response is received
  useEffect(() => {
    if (AIResponse) {
      setEditableContent(AIResponse);
    }
  }, [AIResponse]);

  const _handleChangeCallback = useCallback((searchVal) => {
    animRef.current?.slideOutUp?.(400);
    if (searchVal) {
      setTimeout(() => setSearchActive(false), 50);
    } else {
      setTimeout(() => setSearchActive(true), 50);
    }
    setTimeout(() => animRef.current?.slideInDown?.(500), 100);
  }, []);

  const handleSliderChange = useMemo(() => debounce((value) => {
    setDistanceValue(value);
    distanceCallback(value);
  }, 10), [distanceCallback]);

  // Convert product object to string for AI API
  const convertProductToString = useCallback((product: any): string => {
    if (!product) {
      return '';
    }

    if (typeof product === 'string') {
      return product;
    }

    // Handle product_select field structure - could be nested or direct
    let productData = product;

    // If it's an object with product_select key
    if (product.product_select) {
      productData = Array.isArray(product.product_select)
        ? product.product_select[0]
        : product.product_select;
    }

    // Try different possible structures
    const data = productData?.data || productData?.Product?.data || productData;

    if (!data || (typeof data === 'object' && Object.keys(data).length === 0)) {
      return JSON.stringify(product);
    }

    // Build descriptive string from product data
    const parts: string[] = [];

    // Try various field name variations
    const productName = data.product_name || data.name || data.title || data.dataTitle;
    const description = data.product_description || data.description || data.desc;
    const price = data.product_price || data.price;
    const features = data.product_features || data.features;
    const category = data.product_category || data.category;
    const brand = data.product_brand || data.brand;

    if (productName) parts.push(`Product: ${productName}`);
    if (description) parts.push(`Description: ${description}`);
    if (price) parts.push(`Price: ${price}`);
    if (features && Array.isArray(features)) {
      parts.push(`Features: ${features.join(', ')}`);
    } else if (features && typeof features === 'string') {
      parts.push(`Features: ${features}`);
    }
    if (category) parts.push(`Category: ${category}`);
    if (brand) parts.push(`Brand: ${brand}`);

    // Fallback to dataTitle if available
    if (parts.length === 0 && productData?.dataTitle) {
      parts.push(`Product: ${productData.dataTitle}`);
    }

    return parts.length > 0 ? parts.join('\n') : JSON.stringify(product);
  }, []);

  // Extracted function for generating AI content
  const handleGenerateContent = useCallback(async (body) => {
    setIsLoading(true);
    setOriginalPromptBody(body); // Save for potential regeneration
    try {
      // Convert product object to string if needed
      let textToSend = body.text;

      if (textToSend && typeof textToSend !== 'string') {
        textToSend = convertProductToString(textToSend);
      }

      if (!textToSend || (typeof textToSend === 'string' && !textToSend.trim())) {
        Alert.alert("Input Required", "Please provide valid product information or text to generate content.");
        setIsLoading(false);
        return;
      }

      const response = await monitorFail.post(`/ai/quick-social-content`, { text: textToSend }).then(res => res.data);

      if (!response) {
        throw new Error("No response from AI");
      }

      // It's safer to check for the actual content property from your API response
      let content = response.post_text || response.raw?.toString().replace(/^"|"$/g, "") || "";
      if (response.hashtags) {
        //Append hashtags to the content  
        content += "\n\n" + response.hashtags.join(" ");
      }
      setAIResponse(content);
    } catch (error) {
      console.error("Error fetching AI content:", error);
      Alert.alert("Error", "Failed to generate content. Please try again.");
      setAIResponse(null);
    } finally {
      setIsLoading(false);
    }
  }, [convertProductToString]);

  // Function to handle the final submission of the content
  const handleSubmitContent = useCallback(async () => {
    const finalContent = editableContent.trim();
    if (!finalContent) {
      Alert.alert("Empty Post", "You can't submit an empty post.");
      return;
    }

    // Check rate limit before posting
    const rateLimitResult = await checkPostRateLimit(isFreePlan);
    if (!rateLimitResult.allowed) {
      const errorMessage = formatRateLimitMessage(rateLimitResult, isFreePlan);
      Alert.alert('Rate Limit', errorMessage);
      return;
    }

    const location = await handleGetLocation(dispatch, selector);

    // --- Placeholder for your actual API call to post content ---
    await apis.postToFeed({
      content: finalContent,
      product: activeProduct ? activeProduct._id : null,
      location: location.coords || null,
      integrations: selectedIntegrations || [],
    })
    
    // Store last post timestamp after successful post
    await storeLastPostTimestamp();
    
    Alert.alert("Posted!", "Your content has been posted to the feed.");
    // Example: await apis.postToFeed({ content: finalContent });
    // --- End of placeholder ---

    // Reset all states and close the sheet
    setOpen(false);
    setAIResponse(null);
    setEditableContent('');
    setOriginalPromptBody(null);
    setActiveProduct(null);
    setTextInputValue(''); // Clear the input field

    // Refetch the main feed to show the new post
    refetch();
  }, [editableContent, refetch, isFreePlan, dispatch, selector, activeProduct, selectedIntegrations]);

  // Handle opening the AI post modal with orientation preservation (especially important for iPad)
  const handleOpenAIModal = useCallback(async () => {
    // Unlock orientation to prevent forced orientation changes on iPad when opening modal
    try {
      await ScreenOrientation.unlockAsync();
    } catch (error) {
      // If orientation API fails, continue anyway
    }
    setOpen(true);
  }, []);

  return (
    <View style={{
      flexDirection: "row",
      minHeight: 20,
      paddingVertical: "3%",
      alignSelf: "stretch",
      top: Platform.select({ web: 0, default: -22 }),
      elevation: 10,
      overflow: "visible",
      borderRadius: 20,
      backgroundColor: Platform.select({ android: "white", default: undefined }),
      minWidth: Platform.select({ web: 300, default: "100%", android: "97%" }),
      paddingHorizontal: 10,
      maxWidth: Platform.select({
        android: "87%",
        web: "100%"
      })
    }}>
      <BlurView tint={theme ? 'light' : "dark"} style={{ flex: 1, overflow: "visible", minHeight: 130, maxHeight: Platform.OS === 'web' ? '25%' : 280, alignSelf: "stretch", paddingVertical: 10, backgroundColor: theme ? '#ffffff20' : 'transparent', shadowRadius: 20, shadowOpacity: 0.2, gap: 20, shadowColor: theme ? 'grey' : 'white', borderRadius: 20, zIndex: 1 }}>
        <View style={[styles.centerEverything, { minHeight: 80, flexDirection: 'row', flex: 1, alignSelf: 'stretch', zIndex: 3, paddingBottom: 10, padding: 5 }]}>
          <View style={{ flex: 10, backgroundColor: "transparent" }}>
            {searchActive ? (
              <Animatable.View useNativeDriver ref={animRef} animation={'slideInDown'} style={{ left: Platform.select({ web: undefined, default: -40 }), maxWidth: 400, flex: 5, top: Platform.OS === 'web' ? 0 : -10 }}>
                <SearchComponent minWidth={400} customStyle={{ borderRadius: 15, width: "130%", minHeight: 40 }} searchOnly placeHolder='Search Feed' />
              </Animatable.View>
            ) : (
              <Animatable.View useNativeDriver ref={animRef} animation={'slideInDown'} style={{ flex: 5, maxWidth: "90%", left: Platform.select({ web: undefined, native: 30 }), alignSelf: 'stretch' }}>
                <PostToFeed selectedIntegrations={selectedIntegrations} setSelectedIntegrations={setSelectedIntegrations} appendToFeed={(data) => {
                  setItem((prevItems) => [data, ...prevItems]);
                }} refetch={refetch} />
              </Animatable.View>
            )}
          </View>
          <View style={{ flex: 1, left: 5 }}>
            <FontAwesome name="exchange" size={24} style={{ top: Platform.select({ default: "24%", web: -10 }), left: 5, flex: 1, borderRadius: 5, overflow: 'hidden' }} color={theme ? "black" : "white"} onPress={() => _handleChangeCallback(searchActive)} />
            {!searchActive && (
              <>
                <Pressable style={{ flex: 1 }} onPress={handleOpenAIModal}>
                  <LottieView
                    source={require("@assets/animations/automation_robot.json")}
                    autoPlay
                    loop
                    style={{ flex: 1, width: 35 }}
                  />
                </Pressable>

                <Modal
                  visible={open}
                  transparent
                  animationType="fade"
                  onRequestClose={() => !isLoading && setOpen(false)}
                  supportedOrientations={['portrait', 'landscape', 'landscape-left', 'landscape-right', 'portrait-upside-down']}
                >
                  <KeyboardAvoidingView
                    style={{ flex: 1 }}
                    behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
                  >
                    <Pressable
                      style={{
                        flex: 1,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: 20,
                      }}
                      onPress={() => !isLoading && setOpen(false)}
                    >
                      <Pressable
                        onPress={(e) => e.stopPropagation()}
                        style={{
                          backgroundColor: theme ? '#ffffff' : '#1f2937',
                          borderRadius: 16,
                          width: '100%',
                          maxWidth: 500,
                          maxHeight: '90%',
                          shadowColor: '#000',
                          shadowOffset: { width: 0, height: 4 },
                          shadowOpacity: 0.3,
                          shadowRadius: 8,
                          elevation: 8,
                        }}
                      >
                        <Animatable.View animation="slideInUp" duration={300}>
                          {/* Header */}
                          <XStack
                            padding="$4"
                            alignItems="center"
                            justifyContent="space-between"
                            borderBottomWidth={1}
                            borderBottomColor={theme ? '#e5e7eb' : '#4b5563'}
                          >
                            <H4 color={theme ? '#1f2937' : '#f9fafb'}>Generate Social Content</H4>
                            <Pressable onPress={() => !isLoading && setOpen(false)} disabled={isLoading}>
                              <MaterialIcons
                                name="close"
                                size={24}
                                color={theme ? '#6b7280' : '#9ca3af'}
                              />
                            </Pressable>
                          </XStack>

                          <ScrollView
                            showsVerticalScrollIndicator={false}
                            contentContainerStyle={{ padding: 16 }}
                            style={{ maxHeight: 600 }}
                          >
                            <YStack gap="$4">{AIResponse ? (
                              // --- VIEW AFTER AI RESPONSE IS GENERATED ---
                              <YStack width="100%" gap="$4" alignItems="center">
                                {activeProduct?.["Product"]?.dataImages?.length > 0 && (
                                  <YStack width="100%" alignItems="center" gap="$2">
                                    <SizableText color={theme ? '#1f2937' : '#f9fafb'} fontWeight="600">
                                      Product Images
                                    </SizableText>
                                    <View style={{ height: 150, width: '100%', alignItems: "center", justifyContent: "center" }}>
                                      <HorizontalCarousel
                                        images={activeProduct["Product"].dataImages}
                                        imageStyle={{ resizeMode: "contain", maxHeight: 150 }}
                                        height={150}
                                      />
                                    </View>
                                  </YStack>
                                )}

                                {/* Single editable text area for refining content */}
                                <YStack width="100%" gap="$2">
                                  <SizableText color={theme ? '#1f2937' : '#f9fafb'} fontWeight="600">
                                    Generated Content
                                  </SizableText>
                                  <TextArea
                                    minHeight={200}
                                    maxHeight={300}
                                    value={editableContent}
                                    onChangeText={setEditableContent}
                                    placeholder="Refine your AI message..."
                                    backgroundColor={theme ? '#f8fafc' : '#374151'}
                                    borderColor={theme ? '#e5e7eb' : '#4b5563'}
                                    color={theme ? '#1f2937' : '#ffffff'}
                                    textAlignVertical="top"
                                  />
                                </YStack>
                              </YStack>
                            ) : (
                              // --- INITIAL VIEW FOR GENERATING CONTENT ---
                              <YStack width="100%" gap="$4" alignItems="center">
                                {isLoading ? (
                                  <YStack alignItems="center" justifyContent="center" minHeight={200} gap="$4">
                                    <ActivityIndicator
                                      size="large"
                                      color={theme ? "#1f2937" : "#f9fafb"}
                                    />
                                    <SizableText color={theme ? '#6b7280' : '#9ca3af'}>
                                      Generating content...
                                    </SizableText>
                                  </YStack>
                                ) : (
                                  <YStack width="100%" gap="$4">
                                    {/* Product Selection */}
                                    <YStack zIndex={200} gap="$2">
                                      <View style={{ minHeight: 200, width: '100%' }}>
                                        <GenerateSchemaForm
                                          ref={formRef}
                                          customStyling={{ minHeight: 150 }}
                                          schema={[
                                            { type: "product_select", content: { helpText: "Simply select a product and I can do the heavy lifting" }, title: "Product", ref: "product_select" },
                                          ]}
                                          hideButton
                                        />
                                      </View>
                                    </YStack>
                                    {/* Divider */}
                                    <XStack alignItems="center" gap="$3" marginVertical="$2">
                                      <View style={{ flex: 1, height: 1, backgroundColor: theme ? '#e5e7eb' : '#4b5563' }} />
                                      <SizableText color={theme ? '#6b7280' : '#9ca3af'} size="$3">
                                        Or
                                      </SizableText>
                                      <View style={{ flex: 1, height: 1, backgroundColor: theme ? '#e5e7eb' : '#4b5563' }} />
                                    </XStack>
                                    <YStack gap="$2">
                                      <SizableText color={theme ? '#1f2937' : '#f9fafb'} fontWeight="600">
                                        {t("whatSay")}
                                      </SizableText>
                                      <TextArea
                                        value={textInputValue}
                                        onChangeText={setTextInputValue}
                                        placeholder="Enter your message or idea..."
                                        backgroundColor={theme ? '#f8fafc' : '#374151'}
                                        borderColor={theme ? '#e5e7eb' : '#4b5563'}
                                        minHeight={100}
                                        textAlignVertical="top"
                                      />
                                    </YStack>
                                  </YStack>
                                )}
                              </YStack>
                            )}</YStack>
                          </ScrollView>

                          {/* Footer */}
                          <XStack
                            padding="$4"
                            justifyContent="space-between"
                            borderTopWidth={1}
                            borderTopColor={theme ? '#e5e7eb' : '#4b5563'}
                            gap="$2"
                          >
                            {AIResponse ? (
                              // Action buttons for after generation
                              <>
                                <Button
                                  flex={1}
                                  variant="outlined"
                                  borderColor={theme ? '#e5e7eb' : '#4b5563'}
                                  color={theme ? '#6b7280' : '#9ca3af'}
                                  onPress={() => setAIResponse(null)}
                                  disabled={isLoading}
                                >
                                  Reset
                                </Button>
                                <Button
                                  flex={1}
                                  backgroundColor={theme ? '#6b7280' : '#4b5563'}
                                  color="white"
                                  onPress={() => {
                                    if (originalPromptBody) {
                                      handleGenerateContent(originalPromptBody);
                                    }
                                  }}
                                  disabled={isLoading}
                                  icon={isLoading ? (
                                    <ActivityIndicator color="white" size="small" />
                                  ) : undefined}
                                >
                                  {isLoading ? 'Regenerating...' : 'Regenerate'}
                                </Button>
                                <Button
                                  flex={1}
                                  backgroundColor="#10b981"
                                  color="white"
                                  onPress={handleSubmitContent}
                                  disabled={isLoading}
                                >
                                  Submit
                                </Button>
                              </>
                            ) : (
                              // Initial buttons
                              <>
                                <Button
                                  flex={1}
                                  variant="outlined"
                                  borderColor={theme ? '#e5e7eb' : '#4b5563'}
                                  color={theme ? '#6b7280' : '#9ca3af'}
                                  onPress={() => setOpen(false)}
                                  disabled={isLoading}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  flex={1}
                                  backgroundColor="#3b82f6"
                                  color="white"
                                  onPress={() => {
                                    const formValues = formRef.current?.values;
                                    let textToSend = textInputValue.trim();

                                    // Only use the form value if a product is actually selected
                                    if (formValues && formValues.product_select) {
                                      textToSend = formValues;
                                    }

                                    console.log("Generating content with payload:", { text: textToSend });

                                    // Validate the final value being sent
                                    if (!textToSend || (typeof textToSend === 'string' && !textToSend.trim())) {
                                      Alert.alert("Input Required", "Please type a message or select a product to generate content.");
                                      return;
                                    }

                                    // Additional validation: if it's an object, make sure it has valid product data
                                    if (textToSend && typeof textToSend === 'object' && textToSend !== null && !Array.isArray(textToSend)) {
                                      const productObj = textToSend as Record<string, any>;
                                      const hasProductData = productObj.product_select ||
                                        productObj.data ||
                                        productObj.Product?.data;
                                      if (!hasProductData) {
                                        Alert.alert("Invalid Product", "Please select a valid product or enter text to generate content.");
                                        return;
                                      }
                                    }

                                    setActiveProduct(formValues);
                                    handleGenerateContent({ text: textToSend });
                                  }}
                                  disabled={isLoading}
                                  icon={isLoading ? (
                                    <ActivityIndicator color="white" size="small" />
                                  ) : undefined}
                                >
                                  {isLoading ? 'Generating...' : 'Generate'}
                                </Button>
                              </>
                            )}
                          </XStack>
                        </Animatable.View>
                      </Pressable>
                    </Pressable>
                  </KeyboardAvoidingView>
                </Modal>
              </>
            )}
          </View>
        </View>
        {searchActive && (
          <View
            style={[styles.centerEverything, { top: -30, minHeight: 30, maxHeight: 40, paddingHorizontal: 5, flex: 1, flexDirection: 'row', alignSelf: 'stretch' }]}
            collapsable={false}
          >
            <AntDesign name="home" size={20} color={theme ? "black" : "white"} style={{ flex: 1, left: 3, textAlign: 'center' }} />
            <View
              style={{ flex: 5, minHeight: 40, justifyContent: 'center', zIndex: 10 }}
              collapsable={false}
            >
              <Slider
                value={distanceValue}
                onValueChange={handleSliderChange}
                onSlidingComplete={() => refetch()}
                minimumValue={1}
                maximumValue={100}
                style={{ width: '100%', height: 40 }}
                minimumTrackTintColor={theme ? 'black' : 'white'}
                maximumTrackTintColor={theme ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.3)'}
                thumbTintColor='rgb(90,192,234)'
                collapsable={false}
              />
            </View>
            <FontAwesome name="globe" size={20} color={theme ? "black" : "white"} style={{ flex: 1, textAlign: 'center' }} />
            <Entypo onPress={() => refreshLocation(dispatch)} name="location-pin" size={24} color={theme ? "black" : "white"} style={{ flex: 1, textAlign: 'center', minHeight: 30, backgroundColor: selector?.coords ? 'rgb(90,192,234)' : undefined, borderRadius: 15, padding: 3, maxWidth: 30, maxHeight: 30, overflow: 'hidden' }} />
          </View>
        )}
      </BlurView>
    </View>
  )
}
export default FeedHeader;
